import { GoogleGenerativeAI } from '@google/generative-ai';
import type {
  NewTaskForm,
  TaskUniverse,
  TaskExperience,
  TaskTimeline,
  CollabStyle,
} from '../constants/taskCreation';

/** Trimmed key from env (Create React App inlines at build time). */
export function getGeminiApiKey(): string | null {
  const raw = process.env.REACT_APP_GEMINI_API_KEY;
  if (raw == null || String(raw).trim() === '') return null;
  return String(raw).trim();
}

export function isGeminiConfigured(): boolean {
  return getGeminiApiKey() !== null;
}

export interface GeminiTaskResult {
  title: string;
  goal: string;
  universe: TaskUniverse;
  description: string;
  tags: string[];
  timeline: TaskTimeline;
  experience: TaskExperience;
  collabStyle: CollabStyle;
  suggestedTools: string[];
}

const VALID_UNIVERSES: TaskUniverse[] = [
  'create', 'build', 'design', 'automate', 'learn',
  'produce', 'manage', 'market', 'entertain', 'brand', 'research', 'other',
];
const VALID_TIMELINES: TaskTimeline[] = ['asap', 'this-week', 'this-month', 'no-rush', 'custom'];
const VALID_EXPERIENCES: TaskExperience[] = ['explorer', 'maker', 'pro', 'expert'];
const VALID_COLLAB: CollabStyle[] = ['solo', 'help-wanted', 'team', 'mentor'];

function sanitize(raw: any): GeminiTaskResult {
  return {
    title: String(raw.title || '').slice(0, 80),
    goal: String(raw.goal || '').slice(0, 200),
    universe: VALID_UNIVERSES.includes(raw.universe) ? raw.universe : 'other',
    description: String(raw.description || '').slice(0, 500),
    tags: Array.isArray(raw.tags) ? raw.tags.map(String).slice(0, 8) : [],
    timeline: VALID_TIMELINES.includes(raw.timeline) ? raw.timeline : 'this-week',
    experience: VALID_EXPERIENCES.includes(raw.experience) ? raw.experience : 'maker',
    collabStyle: VALID_COLLAB.includes(raw.collabStyle) ? raw.collabStyle : 'help-wanted',
    suggestedTools: Array.isArray(raw.suggestedTools) ? raw.suggestedTools.map(String).slice(0, 10) : [],
  };
}

function isInvalidApiKeyError(err: unknown): boolean {
  const s = String((err as Error)?.message ?? err);
  return (
    s.includes('API_KEY_INVALID') ||
    s.includes('API key not valid') ||
    s.includes('invalid API key')
  );
}

function isModelNotFoundError(err: unknown): boolean {
  const s = String((err as Error)?.message ?? err);
  return s.includes('404') && (s.includes('not found') || s.includes('is not found'));
}

/**
 * Build a minimal GeminiTaskResult from a raw user prompt
 * when AI is unavailable. Uses the first sentence as title
 * and the full prompt as description.
 */
export function fallbackParseFromPrompt(userPrompt: string): GeminiTaskResult {
  const trimmed = userPrompt.trim();
  const firstSentence = trimmed.split(/[.\n!?]/)[0].trim();
  const title = firstSentence.length > 80 ? firstSentence.slice(0, 77) + '...' : firstSentence || trimmed.slice(0, 80);
  const words = trimmed.toLowerCase().split(/\s+/);
  const tags = words
    .filter(w => w.length > 3)
    .slice(0, 5);

  let universe: TaskUniverse = 'other';
  const lower = trimmed.toLowerCase();
  if (/\b(app|website|api|bot|code|react|node|python|flutter)\b/.test(lower)) universe = 'build';
  else if (/\b(logo|ui|ux|figma|design|mockup|graphic)\b/.test(lower)) universe = 'design';
  else if (/\b(video|youtube|podcast|blog|write|content|article)\b/.test(lower)) universe = 'create';
  else if (/\b(automat|pipeline|ci.?cd|workflow|n8n|script)\b/.test(lower)) universe = 'automate';
  else if (/\b(server|deploy|docker|database|devops|aws|infrastructure)\b/.test(lower)) universe = 'manage';
  else if (/\b(seo|market|campaign|social media|ads|growth)\b/.test(lower)) universe = 'market';
  else if (/\b(learn|study|course|tutorial)\b/.test(lower)) universe = 'learn';
  else if (/\b(game|stream|music|animation)\b/.test(lower)) universe = 'entertain';
  else if (/\b(brand|identity|packaging|strategy)\b/.test(lower)) universe = 'brand';
  else if (/\b(research|data|experiment|analysis|test)\b/.test(lower)) universe = 'research';
  else if (/\b(edit|mix|render|produce|premiere|after effects)\b/.test(lower)) universe = 'produce';

  return {
    title,
    goal: title,
    universe,
    description: trimmed.slice(0, 500),
    tags,
    timeline: 'this-week',
    experience: 'maker',
    collabStyle: 'help-wanted',
    suggestedTools: [],
  };
}

export async function parseTaskFromPrompt(userPrompt: string): Promise<GeminiTaskResult> {
  const apiKey = getGeminiApiKey();
  if (!apiKey) {
    throw new Error('GEMINI_API_KEY_MISSING');
  }

  const genAI = new GoogleGenerativeAI(apiKey);
  const fromEnv = process.env.REACT_APP_GEMINI_MODEL?.trim();
  const candidates = [fromEnv, 'gemini-2.0-flash', 'gemini-1.5-flash', 'gemini-1.5-flash-8b'].filter(Boolean) as string[];
  const modelsToTry = candidates.filter((m, i) => candidates.indexOf(m) === i);

  const prompt = `You are a task creation assistant for a collaborative DevOps and creator platform called GNK Continuum.
Parse the user's task description and return a JSON object with EXACTLY these fields:
- title: string (concise task name, max 80 chars)
- goal: string (clear measurable outcome, max 200 chars)
- universe: one of exactly [create, build, design, automate, learn, produce, manage, market, entertain, brand, research, other]
- description: string (detailed requirements and context, max 500 chars)
- tags: string[] (5-8 lowercase keyword tags, no spaces within a tag)
- timeline: one of exactly [asap, this-week, this-month, no-rush, custom]
- experience: one of exactly [explorer, maker, pro, expert]
- collabStyle: one of exactly [solo, help-wanted, team, mentor]
- suggestedTools: string[] (max 6 tool names relevant to the task, e.g. React, Figma, Python, Firebase, Docker, GitHub, Notion, Slack, AWS)

Universe selection guide:
- build = apps, websites, APIs, bots, software
- design = UI/UX, graphics, logos, 3D, branding visuals
- automate = workflows, CI/CD, scripts, bots, pipelines
- create = videos, writing, podcasts, social content
- manage = servers, databases, DevOps, infrastructure
- market = SEO, ads, social campaigns, growth
- learn = tutorials, courses, experiments, studying
- produce = video editing, audio mixing, post-production
- research = data analysis, ML experiments, A/B tests
- brand = brand identity, strategy, packaging
- entertain = games, streaming, music production

User's task description:
"${userPrompt}"

Respond ONLY with valid JSON. No markdown code blocks, no explanation, no extra text. Just the raw JSON object.`;

  let lastError: unknown;
  for (const modelName of modelsToTry) {
    try {
      const model = genAI.getGenerativeModel({ model: modelName });
      const result = await model.generateContent(prompt);
      const text = result.response
        .text()
        .trim()
        .replace(/^```(?:json)?\s*/i, '')
        .replace(/\s*```\s*$/i, '');

      const parsed = JSON.parse(text);
      return sanitize(parsed);
    } catch (err) {
      lastError = err;
      if (isInvalidApiKeyError(err)) {
        throw new Error('GEMINI_API_KEY_INVALID');
      }
      if (isModelNotFoundError(err)) {
        continue;
      }
      throw err;
    }
  }

  throw lastError instanceof Error ? lastError : new Error(String(lastError));
}

/** Convert a GeminiTaskResult into a partial NewTaskForm (tools need separate mapping) */
export function geminiResultToForm(result: GeminiTaskResult): Partial<NewTaskForm> {
  return {
    universe: result.universe,
    title: result.title,
    goal: result.goal,
    description: result.description,
    tags: result.tags,
    timeline: result.timeline,
    experience: result.experience,
    collabStyle: result.collabStyle,
    // suggestedTools are handled separately as chips in Phase B
  };
}
